/**
 * RUB-46138: Onboarding Assistant chat page.
 *
 * Turn-based chat against the rubicon backend's /chat/onboarding proxy (which
 * forwards to the onboarding agent on Bedrock AgentCore). The reply streams:
 * the proxy returns Server-Sent Events, so the assistant's text "types" in
 * live as `token` deltas arrive, the thinking indicator shows the current
 * `progress` step, and the turn is reconciled to the canonical `final` reply
 * (then artifact blocks and PR links) when it completes. Agent text renders as
 * GFM markdown via MarkdownContent (react-markdown - raw HTML is never
 * rendered, keeping untrusted model output injection-safe); draft artifacts
 * arrive as structured blocks with copy buttons. An early 501/502/400 still
 * comes back as buffered JSON and is handled as before.
 */

import {
  Content,
  Header,
  InfoCard,
  MarkdownContent,
  Page,
} from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { useCallback, useEffect, useRef, useState } from 'react';

const useStyles = makeStyles(theme => ({
  log: {
    maxHeight: '55vh',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    // Flex children default to flex-shrink: 1 - without this, a long
    // conversation crushes each entry (artifact blocks collapse to slivers)
    // instead of letting the log scroll.
    '& > *': { flexShrink: 0 },
  },
  user: {
    alignSelf: 'flex-end',
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(1, 2),
    borderRadius: 12,
    maxWidth: '75%',
    whiteSpace: 'pre-wrap',
  },
  agent: {
    alignSelf: 'flex-start',
    background: theme.palette.background.default,
    padding: theme.spacing(1, 2),
    borderRadius: 12,
    maxWidth: '85%',
    // MarkdownContent handles its own block spacing; avoid double newlines.
    '& > :first-child': { marginTop: 0 },
    '& > :last-child': { marginBottom: 0 },
    '& pre': { overflowX: 'auto' },
    '& table': { borderCollapse: 'collapse' },
    '& th, & td': {
      border: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(0.5, 1),
    },
  },
  error: {
    alignSelf: 'flex-start',
    background: theme.palette.background.default,
    padding: theme.spacing(1, 2),
    borderRadius: 12,
    color: theme.palette.error.main,
    whiteSpace: 'pre-wrap',
  },
  thinking: {
    alignSelf: 'flex-start',
    background: theme.palette.background.default,
    padding: theme.spacing(1, 2),
    borderRadius: 12,
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  artifact: {
    alignSelf: 'stretch',
    padding: theme.spacing(1, 1.5),
    background: theme.palette.type === 'dark' ? '#1e1e1e' : '#f5f5f5',
  },
  artifactBody: {
    fontFamily: 'monospace',
    fontSize: 12,
    whiteSpace: 'pre',
    overflow: 'auto',
    maxHeight: 320,
    margin: 0,
    paddingTop: theme.spacing(1),
  },
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
}));

// Discriminated singletons so the render switch is exhaustively narrowed.
// 'error' renders as plain text (never markdown) - the error channel must not
// feed the markdown sink.
type ChatEntry =
  | { kind: 'user'; text: string }
  | { kind: 'agent'; text: string }
  | { kind: 'error'; text: string }
  | { kind: 'artifact'; path: string; content: string };

type TurnResponse = {
  reply: string;
  artifacts: Record<string, string> | null;
  spec_valid: boolean | null;
  pull_requests: string[] | null;
};

// Strict GitHub PR URL (mirrors the backend) and unsafe object keys.
const PR_URL_PATTERN = /^https:\/\/github\.com\/[\w.-]+\/[\w.-]+\/pull\/\d+$/;
const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

/** Collect string-valued entries into a prototype-safe record (or null). */
function safeArtifacts(value: unknown): Record<string, string> | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const artifacts: Record<string, string> = Object.create(null);
  for (const [path, content] of Object.entries(value)) {
    if (!UNSAFE_KEYS.has(path) && typeof content === 'string') {
      artifacts[path] = content;
    }
  }
  return Object.keys(artifacts).length ? artifacts : null;
}

function safePullRequests(value: unknown): string[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  return value.filter(
    (url): url is string => typeof url === 'string' && PR_URL_PATTERN.test(url),
  );
}

/**
 * Runtime guard for the backend's TurnResponse. The backend normalizes the
 * agent payload to this contract, but the frontend still verifies instead of
 * asserting - malformed payloads surface as an error entry, not a crash.
 */
function parseTurnResponse(data: unknown): TurnResponse {
  if (typeof data !== 'object' || data === null) {
    throw new Error('unexpected response shape from the onboarding backend');
  }
  const body = data as Record<string, unknown>;
  if (typeof body.reply !== 'string') {
    throw new Error('unexpected response shape from the onboarding backend');
  }
  return {
    reply: body.reply,
    artifacts: safeArtifacts(body.artifacts),
    spec_valid: typeof body.spec_valid === 'boolean' ? body.spec_valid : null,
    pull_requests: safePullRequests(body.pull_requests),
  };
}

/** Descriptive link text for a PR URL, e.g. "rubicon-agents#12". */
function prLinkText(url: string): string {
  const match = url.match(/github\.com\/[^/]+\/([^/]+)\/pull\/(\d+)/);
  return match ? `${match[1]}#${match[2]}` : 'pull request';
}

function newSessionId(): string {
  // AgentCore requires >= 33 chars.
  return `onboarding-${crypto.randomUUID()}`;
}

/** Compile-time exhaustiveness: an unhandled ChatEntry variant is an error. */
function assertNever(value: never): never {
  throw new Error(`unhandled chat entry: ${JSON.stringify(value)}`);
}

/** One decoded Server-Sent Event: its `event:` name and joined `data:` body. */
type SseMessage = { event: string; data: string };

/** Extract the event name and concatenated data of one SSE frame (or null). */
function parseSseFrame(frame: string): SseMessage | null {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of frame.split('\n')) {
    // Blank lines and comments (heartbeats begin ": ") carry no fields.
    if (!line || line.startsWith(':')) {
      continue;
    }
    if (line.startsWith('event:')) {
      event = line.slice(6).replace(/^ /, '');
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).replace(/^ /, ''));
    }
  }
  return dataLines.length ? { event, data: dataLines.join('\n') } : null;
}

/**
 * Read a `text/event-stream` body and yield one message per frame. Frames are
 * separated by a blank line; network chunks split them at arbitrary byte
 * boundaries, so partial frames are buffered across reads.
 */
async function* readSseStream(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<SseMessage> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      buffer = buffer.replace(/\r\n/g, '\n');
      for (;;) {
        const sep = buffer.indexOf('\n\n');
        if (sep === -1) {
          break;
        }
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const message = parseSseFrame(frame);
        if (message) {
          yield message;
        }
      }
    }
    const tail = parseSseFrame(buffer);
    if (tail) {
      yield tail;
    }
  } finally {
    reader.releaseLock();
  }
}

type SessionSummary = {
  id: string;
  preview: string;
  createdAt: string;
  lastActivityAt: string;
};

type StoredTurn = {
  role: 'user' | 'agent';
  content: string;
  artifacts: Record<string, string> | null;
  pullRequests: string[] | null;
  createdAt: string;
};

/** Validate one wire session summary (used over the untrusted /sessions read). */
function parseSessionSummary(value: unknown): SessionSummary | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const v = value as Record<string, unknown>;
  if (
    typeof v.id !== 'string' ||
    typeof v.preview !== 'string' ||
    typeof v.createdAt !== 'string' ||
    typeof v.lastActivityAt !== 'string'
  ) {
    return null;
  }
  return {
    id: v.id,
    preview: v.preview,
    createdAt: v.createdAt,
    lastActivityAt: v.lastActivityAt,
  };
}

/** Validate one wire turn, narrowing role and re-using the safe collectors. */
function parseStoredTurn(value: unknown): StoredTurn | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const v = value as Record<string, unknown>;
  if (typeof v.content !== 'string') {
    return null;
  }
  return {
    role: v.role === 'user' ? 'user' : 'agent',
    content: v.content,
    artifacts: safeArtifacts(v.artifacts),
    pullRequests: safePullRequests(v.pullRequests),
    createdAt: typeof v.createdAt === 'string' ? v.createdAt : '',
  };
}

/** Expand a stored transcript into the same entry shapes the live chat uses. */
function transcriptToEntries(turns: StoredTurn[]): ChatEntry[] {
  const entries: ChatEntry[] = [];
  for (const turn of turns) {
    entries.push({ kind: turn.role, text: turn.content });
    for (const [path, content] of Object.entries(turn.artifacts ?? {})) {
      entries.push({ kind: 'artifact', path, content });
    }
    for (const url of turn.pullRequests ?? []) {
      entries.push({
        kind: 'agent',
        text: `Pull request created: [${prLinkText(url)}](${url})`,
      });
    }
  }
  return entries;
}

/**
 * The conversation card. It owns all chat state so that a keystroke, a
 * streamed token or a progress step re-renders only this subtree. Backstage's
 * <Page> hands its ThemeProvider a new theme on every render, which re-renders
 * every styled component beneath it and makes makeStyles attach fresh
 * stylesheets; with the state below <Page>, that happens once per page view
 * instead of once per update (RUB-50953).
 */
const OnboardingChat = () => {
  const classes = useStyles();
  const { fetch } = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [entries, setEntries] = useState<ChatEntry[]>([
    {
      kind: 'agent',
      text:
        "Hi! I'm the Onboarding Assistant. I can onboard new AI agents and " +
        'applications, or update existing application configs (e.g. "add an ' +
        'SQS queue to my-service"). What would you like to do?',
    },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  // The most recent streamed `progress` step, shown in the thinking indicator.
  const [step, setStep] = useState('');
  // RUB-46145: past sessions from the plugin database. Viewing one is
  // read-only - replay into a fresh agent session is the rest of the story.
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [viewing, setViewing] = useState<string>('');
  const [viewedEntries, setViewedEntries] = useState<ChatEntry[]>([]);
  // Transient, assertively-announced status (copy feedback etc.).
  const [status, setStatus] = useState('');
  const sessionId = useRef(newSessionId());
  // Live-streaming turn state: the index of the in-progress agent bubble in
  // `entries` (-1 until it exists) and the text accumulated from `token`
  // deltas so far.
  const liveIndex = useRef(-1);
  const liveText = useRef('');
  const logRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Guards against a slower transcript fetch overwriting a newer selection.
  const activeView = useRef('');

  const refreshSessions = useCallback(async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('rubicon');
      const response = await fetch(`${baseUrl}/chat/sessions`);
      if (!response.ok) return;
      const data = (await response.json()) as { sessions?: unknown };
      const list = Array.isArray(data.sessions) ? data.sessions : [];
      setSessions(
        list
          .map(parseSessionSummary)
          .filter((s): s is SessionSummary => s !== null),
      );
    } catch {
      // Session browsing is an extra - never block the chat on it.
    }
  }, [discoveryApi, fetch]);

  useEffect(() => {
    refreshSessions();
  }, [refreshSessions]);

  const openSession = useCallback(
    async (id: string) => {
      setViewing(id);
      activeView.current = id;
      if (!id) {
        setViewedEntries([]);
        return;
      }
      try {
        const baseUrl = await discoveryApi.getBaseUrl('rubicon');
        const response = await fetch(
          `${baseUrl}/chat/sessions/${encodeURIComponent(id)}`,
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = (await response.json()) as { turns?: unknown };
        const turns = Array.isArray(data.turns) ? data.turns : [];
        const parsed = turns
          .map(parseStoredTurn)
          .filter((t): t is StoredTurn => t !== null);
        // Ignore a response for a session the user already switched away from.
        if (activeView.current !== id) return;
        setViewedEntries(transcriptToEntries(parsed));
      } catch (error) {
        if (activeView.current !== id) return;
        const detail =
          error instanceof Error ? error.message : 'unexpected error';
        setViewedEntries([
          { kind: 'error', text: `Could not load this session: ${detail}` },
        ]);
      }
    },
    [discoveryApi, fetch],
  );

  useEffect(() => {
    if (!busy) {
      setElapsed(0);
      return undefined;
    }
    const startedAt = Date.now();
    const timer = setInterval(
      () => setElapsed(Math.round((Date.now() - startedAt) / 1000)),
      1000,
    );
    return () => clearInterval(timer);
  }, [busy]);

  // Append a `token` delta into the single live agent bubble, creating it on
  // the first token. Runs inside one state update so the index is assigned and
  // read atomically (no stale -1 races between rapid deltas).
  const appendToken = useCallback((delta: string) => {
    liveText.current += delta;
    const text = liveText.current;
    setEntries(prev => {
      if (liveIndex.current === -1) {
        liveIndex.current = prev.length;
        return [...prev, { kind: 'agent', text }];
      }
      const idx = liveIndex.current;
      return prev.map((entry, i) =>
        i === idx && entry.kind === 'agent' ? { ...entry, text } : entry,
      );
    });
  }, []);

  const send = useCallback(async () => {
    const message = input.trim();
    if (!message || busy) return;
    setInput('');
    setEntries(prev => [...prev, { kind: 'user', text: message }]);
    setBusy(true);
    setStep('');
    // Reset the live-bubble tracker for this turn.
    liveIndex.current = -1;
    liveText.current = '';
    try {
      const baseUrl = await discoveryApi.getBaseUrl('rubicon');
      const response = await fetch(`${baseUrl}/chat/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: sessionId.current, message }),
      });

      // An early failure (501/502/400) or any non-stream response still comes
      // back as buffered JSON - surface its error exactly as before. Only an
      // ok text/event-stream body switches into streaming mode.
      const contentType = response.headers.get('content-type') ?? '';
      if (
        !response.ok ||
        !contentType.includes('text/event-stream') ||
        !response.body
      ) {
        const detail = (await response.json().catch(() => null)) as {
          error?: string;
        } | null;
        throw new Error(detail?.error ?? `HTTP ${response.status}`);
      }

      let sawFinal = false;
      for await (const { event, data } of readSseStream(response.body)) {
        if (event === 'token') {
          const parsed = JSON.parse(data) as { text?: unknown };
          if (typeof parsed.text === 'string') {
            appendToken(parsed.text);
          }
        } else if (event === 'progress') {
          const parsed = JSON.parse(data) as { step?: unknown };
          setStep(typeof parsed.step === 'string' ? parsed.step : '');
        } else if (event === 'error') {
          const parsed = JSON.parse(data) as { error?: unknown };
          throw new Error(
            typeof parsed.error === 'string'
              ? parsed.error
              : 'the onboarding agent reported an error',
          );
        } else if (event === 'final') {
          const turn = parseTurnResponse(JSON.parse(data));
          sawFinal = true;
          // Reconcile the live bubble to the canonical reply (replacing the
          // accumulated streamed text so the persisted view matches), then
          // append artifact blocks and PR links exactly as before.
          setEntries(prev => {
            let next: ChatEntry[];
            if (liveIndex.current === -1) {
              liveIndex.current = prev.length;
              next = [...prev, { kind: 'agent', text: turn.reply }];
            } else {
              const idx = liveIndex.current;
              next = prev.map((entry, i) =>
                i === idx && entry.kind === 'agent'
                  ? { ...entry, text: turn.reply }
                  : entry,
              );
            }
            for (const [path, content] of Object.entries(
              turn.artifacts ?? {},
            )) {
              next.push({ kind: 'artifact', path, content });
            }
            for (const url of turn.pull_requests ?? []) {
              next.push({
                kind: 'agent',
                text: `Pull request created: [${prLinkText(url)}](${url})`,
              });
            }
            return next;
          });
        }
      }

      if (!sawFinal) {
        throw new Error('the connection ended before the agent finished');
      }
      refreshSessions();
    } catch (error) {
      const detail =
        error instanceof Error ? error.message : 'unexpected error';
      setEntries(prev => [
        ...prev,
        { kind: 'error', text: `Something went wrong: ${detail}` },
      ]);
    } finally {
      setBusy(false);
      setStep('');
      // Return focus to the input so keyboard users can keep typing; the
      // input was disabled mid-turn, dropping focus to <body>.
      inputRef.current?.focus();
    }
  }, [appendToken, busy, discoveryApi, fetch, input, refreshSessions]);

  const copyArtifact = useCallback(async (path: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setStatus(`Copied ${path} to the clipboard`);
    } catch {
      setStatus(`Could not copy ${path}`);
    }
  }, []);

  const displayed = viewing ? viewedEntries : entries;

  // Follow the newest content after every append/turn-state change (replaces
  // a leaked setTimeout; also scrolls on user-send and the thinking indicator).
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      logRef.current?.scrollTo({ top: logRef.current.scrollHeight });
    });
    return () => cancelAnimationFrame(id);
  }, [displayed, busy]);

  return (
    <InfoCard>
      {sessions.length > 0 && (
        <Box display="flex" gridGap={8} mb={2} alignItems="center">
          <TextField
            select
            size="small"
            variant="outlined"
            label="Previous sessions"
            style={{ minWidth: 320 }}
            value={viewing}
            onChange={event => openSession(event.target.value)}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
          >
            <option value="">Current chat</option>
            {sessions.map(session => (
              <option key={session.id} value={session.id}>
                {new Date(session.lastActivityAt).toLocaleString()} —{' '}
                {session.preview}
              </option>
            ))}
          </TextField>
          {viewing && (
            <Typography variant="caption" color="textSecondary">
              Viewing a stored session (read-only) — switch back to “Current
              chat” to continue. Resuming with full agent memory lands with
              RUB-46145.
            </Typography>
          )}
        </Box>
      )}
      {/* tabIndex makes the scrollable log keyboard-reachable (WCAG 2.1.1);
        the rule fires because "log" isn't an interactive role. */}
      <div
        className={classes.log}
        ref={logRef}
        role="log"
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        // Only announce incremental additions in the live chat; viewing a
        // stored transcript swaps the whole log and must not be read out.
        aria-live={viewing ? 'off' : 'polite'}
        aria-relevant="additions"
        aria-label="Conversation"
      >
        {displayed.map((entry, index) => {
          if (entry.kind === 'artifact') {
            return (
              <Paper
                key={index}
                className={classes.artifact}
                variant="outlined"
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption">{entry.path}</Typography>
                  <Tooltip title={`Copy ${entry.path}`}>
                    <IconButton
                      size="small"
                      aria-label={`Copy ${entry.path}`}
                      onClick={() => copyArtifact(entry.path, entry.content)}
                    >
                      <FileCopyIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <pre className={classes.artifactBody}>{entry.content}</pre>
              </Paper>
            );
          }
          if (entry.kind === 'user') {
            return (
              <div key={index} className={classes.user}>
                {entry.text}
              </div>
            );
          }
          if (entry.kind === 'error') {
            // Plain text on purpose: error strings never enter the
            // markdown-rendered (untrusted) channel. role="alert" so it's
            // announced assertively, not buried in the polite log.
            return (
              <div key={index} className={classes.error} role="alert">
                {entry.text}
              </div>
            );
          }
          if (entry.kind === 'agent') {
            return (
              <div key={index} className={classes.agent}>
                {/* Untrusted model output: MarkdownContent (react-markdown,
                  GFM) never renders raw HTML, so this stays injection-safe. */}
                <MarkdownContent content={entry.text} dialect="gfm" />
              </div>
            );
          }
          // A new ChatEntry variant must be handled explicitly rather than
          // silently falling into the markdown channel.
          return assertNever(entry);
        })}
        {busy && (
          <div className={classes.thinking} role="status">
            <CircularProgress size={14} thickness={5} aria-hidden />
            {/* Announced text: the current streamed step when there is one,
              else the generic message. Steps change infrequently, so
              announcing them is useful; the per-second counter stays
              aria-hidden so it isn't re-announced every second. */}
            <span>
              {step
                ? `The onboarding agent is working: ${step}…`
                : 'The onboarding agent is thinking…'}
            </span>
            <span aria-hidden>
              {elapsed > 2 ? ` ${elapsed}s` : ''}
              {elapsed > 20 &&
                ' (validation rounds can take up to half a minute)'}
            </span>
          </div>
        )}
      </div>
      {/* Assertive, visually-hidden region for transient action feedback. */}
      <div role="status" aria-live="assertive" className={classes.srOnly}>
        {status}
      </div>
      <Box display="flex" gridGap={8} mt={2}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Describe what you want to onboard or change…"
          inputRef={inputRef}
          inputProps={{
            'aria-label': 'Message to the onboarding assistant',
          }}
          value={input}
          // Stay enabled during a turn so keyboard focus isn't dropped to
          // <body>; send() guards re-entrancy. Disabled only when viewing
          // a read-only stored session.
          disabled={!!viewing}
          onChange={event => setInput(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              send();
            }
          }}
          multiline
          maxRows={4}
        />
        <Button
          variant="contained"
          color="primary"
          disabled={busy || !!viewing || !input.trim()}
          onClick={send}
        >
          Send
        </Button>
      </Box>
    </InfoCard>
  );
};

export const OnboardingChatPage = () => (
  <Page themeId="tool">
    <Header
      title="Onboarding Assistant"
      subtitle="Conversational onboarding for agents and applications — PRs on request, merges stay human-reviewed"
    />
    <Content>
      <OnboardingChat />
    </Content>
  </Page>
);
